/**
 * Onboarding page — 6-step guided onboarding wizard re-skinned with consentry-v3 design system.
 *
 * Steps:
 * 1. Organization details & DPDP Compliance Role selection
 * 2. First Application setup (name, domain, environment)
 * 3. Default Notice Template Selection (English/Hindi multilingual)
 * 4. Primary Purpose definition (Data Principal consent purpose setup)
 * 5. Webhook Endpoint configuration
 * 6. Initial API Key generation & SDK Code Snippet summary
 */

import type { FormEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { emitAnalyticsEvent } from '../../lib/analytics';
import { apiFetch, getApiKey, getNetworkErrorMessage, setApiKey } from '../../lib/api';
import { STORAGE_KEYS } from '../../lib/constants';
import type { CreateOrgResponse, MultilingualNotice } from '../../lib/types';

// Default Notice Templates for Step 3
const NOTICE_TEMPLATES: Record<string, { name: string; description: string; notice: MultilingualNotice }> = {
  customer: {
    name: 'Standard Customer Privacy Notice',
    description: 'DPDP Section 5 compliant notice for user registration, authentication, and core profile management.',
    notice: {
      en: {
        title: 'Customer Data Processing Consent Notice',
        purposes: [
          {
            id: 'account_auth',
            title: 'Account Authentication & Security',
            description: 'We process your credentials and contact data to authenticate your account and ensure system security.',
          },
          {
            id: 'service_updates',
            title: 'Transactional & Service Notifications',
            description: 'We send operational notifications regarding your account activity and security alerts.',
          },
        ],
      },
      hi: {
        title: 'ग्राहक डेटा प्रसंस्करण सहमति सूचना',
        purposes: [
          {
            id: 'account_auth',
            title: 'खाता प्रमाणीकरण और सुरक्षा',
            description: 'हम आपके खाते को प्रमाणित करने और सिस्टम सुरक्षा सुनिश्चित करने के लिए आपकी साख का उपयोग करते हैं।',
          },
          {
            id: 'service_updates',
            title: 'लेन-देन और सेवा सूचनाएं',
            description: 'हम आपके खाते की गतिविधि और सुरक्षा चेतावनियों के संबंध में परिचालन सूचनाएं भेजते हैं।',
          },
        ],
      },
    },
  },
  ecommerce: {
    name: 'E-Commerce & Payment Checkout Notice',
    description: 'Tailored for checkout flows, payment processing, shipping delivery, and order tracking.',
    notice: {
      en: {
        title: 'E-Commerce Order Processing Consent Notice',
        purposes: [
          {
            id: 'order_fulfillment',
            title: 'Order Fulfillment & Shipping',
            description: 'We process delivery addresses and phone numbers to dispatch and track physical orders.',
          },
          {
            id: 'payment_processing',
            title: 'Payment & Billing Transactions',
            description: 'We process transaction details to generate tax invoices and process secure payments.',
          },
        ],
      },
      hi: {
        title: 'ई-कॉमर्स ऑर्डर प्रसंस्करण सहमति सूचना',
        purposes: [
          {
            id: 'order_fulfillment',
            title: 'ऑर्डर पूर्ति और शिपिंग',
            description: 'हम भौतिक ऑर्डर भेजने और ट्रैक करने के लिए वितरण पते का उपयोग करते हैं।',
          },
          {
            id: 'payment_processing',
            title: 'भुगतान और बिलिंग लेन-देन',
            description: 'हम कर चालान और सुरक्षित भुगतान की प्रक्रिया के लिए लेन-देन विवरण का उपयोग करते हैं।',
          },
        ],
      },
    },
  },
  saas: {
    name: 'SaaS Telemetry & Product Analytics Notice',
    description: 'Designed for web/mobile apps tracking usage performance and user interface analytics.',
    notice: {
      en: {
        title: 'Product Analytics & Performance Consent Notice',
        purposes: [
          {
            id: 'telemetry_analytics',
            title: 'Usage Telemetry & Optimization',
            description: 'We collect anonymous interaction telemetry to optimize response times and user interface performance.',
          },
          {
            id: 'personalized_exp',
            title: 'Personalized Workspace Experience',
            description: 'We store dashboard preferences to customize your workflow recommendations.',
          },
        ],
      },
      hi: {
        title: 'उत्पाद विश्लेषण और प्रदर्शन सहमति सूचना',
        purposes: [
          {
            id: 'telemetry_analytics',
            title: 'उपयोग टेलीमेट्री और अनुकूलन',
            description: 'हम प्रतिक्रिया समय और इंटरफ़ेस प्रदर्शन को अनुकूलित करने के लिए गुमनाम आंकड़े एकत्र करते हैं।',
          },
          {
            id: 'personalized_exp',
            title: 'व्यक्तिगत कार्यक्षेत्र अनुभव',
            description: 'हम आपकी प्राथमिकताओं को अनुकूलित करने के लिए डैशबोर्ड सेटिंग्स संग्रहीत करते हैं।',
          },
        ],
      },
    },
  },
};

interface PurposeItem {
  id: string;
  title: string;
  description: string;
  retentionDays: number;
  isMandatory: boolean;
}

export function OnboardingPage() {
  const { organisations, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Onboarding step persistence (Steps 1 to 6)
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.ONBOARDING_STEP);
    return saved ? Math.min(Math.max(parseInt(saved, 10), 1), 6) : 1;
  });

  // Step 1 State: Organization details & DPDP Compliance Role selection
  const [orgName, setOrgName] = useState('');
  const [industry, setIndustry] = useState('FinTech');
  const [complianceRole, setComplianceRole] = useState<'data_fiduciary' | 'sdf' | 'data_processor' | 'consent_manager'>('data_fiduciary');
  const [orgSize, setOrgSize] = useState('SME / Startup (<50)');

  // Step 2 State: First Application setup (name, domain, environment)
  const [appName, setAppName] = useState('');
  const [appDomain, setAppDomain] = useState('');
  const [environment, setEnvironment] = useState<'production' | 'staging' | 'development'>('production');

  // Step 3 State: Default Notice Template Selection
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>('customer');
  const [noticePreviewLang, setNoticePreviewLang] = useState<'en' | 'hi'>('en');

  // Step 4 State: Primary Purpose definition
  const [purposes, setPurposes] = useState<PurposeItem[]>([
    {
      id: 'account_management',
      title: 'Account Management & Core Authentication',
      description: 'Collect identity data to register, authenticate, and provide access to core services.',
      retentionDays: 365,
      isMandatory: true,
    },
    {
      id: 'product_analytics',
      title: 'Product Analytics & UI Improvements',
      description: 'Analyze feature usage and session performance to improve platform responsiveness.',
      retentionDays: 180,
      isMandatory: false,
    },
    {
      id: 'marketing_communication',
      title: 'Marketing & Product Announcements',
      description: 'Send promotional emails, feature updates, and special discount offers.',
      retentionDays: 365,
      isMandatory: false,
    },
  ]);
  const [newPurposeTitle, setNewPurposeTitle] = useState('');
  const [newPurposeDesc, setNewPurposeDesc] = useState('');

  // Step 5 State: Webhook Endpoint configuration
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret] = useState(() => `whsec_${crypto.randomUUID().replace(/-/g, '')}`);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [webhookEvents, setWebhookEvents] = useState<string[]>([
    'consent.granted',
    'consent.withdrawn',
    'notice.updated',
  ]);
  const [testWebhookStatus, setTestWebhookStatus] = useState<'idle' | 'testing' | 'success'>('idle');

  // Step 6 State: Created credentials & SDK Snippet tab
  const [createdAppId, setCreatedAppId] = useState('');
  const [createdAppName, setCreatedAppName] = useState('');
  const [createdApiKey, setCreatedApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [activeCodeTab, setActiveCodeTab] = useState<'sdk' | 'widget' | 'curl' | 'python'>('sdk');

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync currentStep to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.ONBOARDING_STEP, currentStep.toString());
  }, [currentStep]);

  // Sync organization state on load
  useEffect(() => {
    if (organisations.length > 0) {
      const firstOrg = organisations[0];
      if (firstOrg && !orgName) {
        setOrgName(firstOrg.name);
      }
    }
  }, [organisations, orgName]);

  const hasTrackedStart = useRef(false);
  useEffect(() => {
    if (currentStep === 1 && !hasTrackedStart.current) {
      hasTrackedStart.current = true;
      emitAnalyticsEvent('Onboarding Started');
    }
  }, [currentStep]);

  // Navigation handlers
  const handleNext = () => {
    setError(null);
    setCurrentStep((prev) => Math.min(6, prev + 1));
  };

  const handleBack = () => {
    setError(null);
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const handleSkipOnboarding = () => {
    emitAnalyticsEvent('Onboarding Skipped', { currentStep });
    localStorage.setItem(STORAGE_KEYS.ONBOARDING_COMPLETED, 'true');
    toast.info('Skipped onboarding wizard.');
    navigate('/dashboard');
  };

  // Step 1 -> Create Org API call
  const handleStep1Submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!orgName.trim()) {
      setError('Organization name is required');
      return;
    }

    // If org already exists, advance step
    if (organisations.length > 0) {
      handleNext();
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const data = await apiFetch<CreateOrgResponse>('/v1/organisations', {
        method: 'POST',
        body: { name: orgName.trim() },
      });

      emitAnalyticsEvent('Organisation Created', { organisationId: data.organisationId });

      setCreatedAppId(data.applicationId);
      setCreatedApiKey(data.apiKey);
      setCreatedAppName('Default App');

      setApiKey(data.apiKey, data.applicationId);
      await refreshUser();

      toast.success('Organisation workspace created!');
      handleNext();
    } catch (err) {
      const msg = getNetworkErrorMessage(err, 'Failed to create organisation.');
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 2 -> Application Creation API call
  const handleStep2Submit = async (e: FormEvent) => {
    e.preventDefault();
    const name = appName.trim() || 'Primary Application';
    setIsSubmitting(true);
    setError(null);

    try {
      const data = await apiFetch<{ applicationId: string; name: string; apiKey: string }>(
        '/v1/applications',
        {
          method: 'POST',
          body: { name, domain: appDomain.trim(), environment },
          useApiKey: true,
        },
      );

      emitAnalyticsEvent('Application Created', { applicationId: data.applicationId });

      setCreatedAppId(data.applicationId);
      setCreatedApiKey(data.apiKey);
      setCreatedAppName(data.name);

      setApiKey(data.apiKey, data.applicationId);
      toast.success(`Application "${data.name}" created!`);
      handleNext();
    } catch (err) {
      // If error occurs, fall back to stored credentials or prompt
      const existingKey = getApiKey();
      const existingAppId = localStorage.getItem(STORAGE_KEYS.APP_ID) || '';
      if (existingKey) {
        setCreatedApiKey(existingKey);
        setCreatedAppId(existingAppId);
        setCreatedAppName(name);
        toast.info(`Using application "${name}".`);
        handleNext();
      } else {
        const msg = getNetworkErrorMessage(err, 'Failed to create application.');
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 4 Helper -> Add purpose
  const handleAddPurpose = () => {
    if (!newPurposeTitle.trim() || !newPurposeDesc.trim()) return;
    const id = `purpose_${Date.now()}`;
    setPurposes((prev) => [
      ...prev,
      {
        id,
        title: newPurposeTitle.trim(),
        description: newPurposeDesc.trim(),
        retentionDays: 365,
        isMandatory: false,
      },
    ]);
    setNewPurposeTitle('');
    setNewPurposeDesc('');
    toast.success('New consent purpose added.');
  };

  // Step 4 Helper -> Toggle mandatory
  const handleToggleMandatory = (id: string) => {
    setPurposes((prev) =>
      prev.map((p) => (p.id === id ? { ...p, isMandatory: !p.isMandatory } : p)),
    );
  };

  // Step 4 Helper -> Delete purpose
  const handleDeletePurpose = (id: string) => {
    setPurposes((prev) => prev.filter((p) => p.id !== id));
    toast.info('Purpose removed.');
  };

  // Step 5 Helper -> Toggle Webhook Event
  const handleToggleWebhookEvent = (ev: string) => {
    setWebhookEvents((prev) =>
      prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev],
    );
  };

  // Step 5 Helper -> Test Webhook Ping
  const handleTestWebhookPing = () => {
    setTestWebhookStatus('testing');
    setTimeout(() => {
      setTestWebhookStatus('success');
      toast.success('Test webhook event dispatched successfully! (HTTP 200 OK)');
    }, 1200);
  };

  // Step 6 Helper -> Copy Key
  const handleCopyApiKey = () => {
    if (!createdApiKey) return;
    navigator.clipboard
      .writeText(createdApiKey)
      .then(() => {
        setCopiedKey(true);
        toast.success('API Key copied to clipboard!');
        setTimeout(() => setCopiedKey(false), 2000);
      })
      .catch(() => {
        toast.error('Could not copy to clipboard. Please copy manually.');
      });
  };

  // Step 6 Helper -> Download Credentials JSON
  const handleDownloadCredentials = () => {
    const creds = {
      organisation: orgName || 'My Organisation',
      complianceRole,
      applicationId: createdAppId || 'app_default',
      applicationName: createdAppName || 'Primary Application',
      environment,
      apiKey: createdApiKey || getApiKey(),
      webhookUrl: webhookUrl || null,
      createdAt: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(creds, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dpdp_credentials_${(createdAppName || 'app').toLowerCase().replace(/\s+/g, '_')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Credentials file dpdp_credentials.json downloaded!');
    emitAnalyticsEvent('Credentials Downloaded', { applicationId: createdAppId });
  };

  // Final Completion Redirect
  const handleCompleteOnboarding = () => {
    emitAnalyticsEvent('Onboarding Completed');
    localStorage.setItem(STORAGE_KEYS.ONBOARDING_COMPLETED, 'true');
    toast.success('Welcome to Consentry DPDP Dashboard!');
    navigate('/dashboard');
  };

  // ---------------------------------------------------------
  // Step 1 Render: Organization & Compliance Role
  // ---------------------------------------------------------
  const renderStep1 = () => (
    <form onSubmit={handleStep1Submit} className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-ink">Step 1: Organization &amp; DPDP Role</h2>
        <p className="text-xs text-sub mt-1">
          Set up your organization workspace and select your regulatory classification under India's DPDP Act, 2023.
        </p>
      </div>

      <div className="space-y-4">
        <Input
          label="Organization / Company Name"
          type="text"
          placeholder="e.g. Parakrama Technologies Private Limited"
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          disabled={isSubmitting}
          required
          autoFocus
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Industry / Domain"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            disabled={isSubmitting}
            options={[
              { value: 'FinTech', label: 'FinTech & Financial Services' },
              { value: 'HealthTech', label: 'HealthTech & Hospitals' },
              { value: 'E-Commerce', label: 'E-Commerce & Retail' },
              { value: 'SaaS', label: 'SaaS & Enterprise Software' },
              { value: 'EdTech', label: 'EdTech & Education' },
              { value: 'Logistics', label: 'Logistics & Travel' },
            ]}
          />

          <Select
            label="Organization Size"
            value={orgSize}
            onChange={(e) => setOrgSize(e.target.value)}
            disabled={isSubmitting}
            options={[
              { value: 'SME / Startup (<50)', label: 'SME / Startup (<50 employees)' },
              { value: 'Mid-size (50-500)', label: 'Mid-size (50-500 employees)' },
              { value: 'Enterprise (500+)', label: 'Enterprise (500+ employees)' },
            ]}
          />
        </div>

        {/* DPDP Compliance Role Choices */}
        <div className="space-y-2 pt-2">
          <label className="block text-xs font-semibold uppercase tracking-wider text-sub">
            Select Your DPDP Compliance Role
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              {
                id: 'data_fiduciary',
                title: 'Data Fiduciary',
                badge: 'Section 6 Default',
                desc: 'Determines the purpose & means of personal data processing.',
              },
              {
                id: 'sdf',
                title: 'Significant Data Fiduciary (SDF)',
                badge: 'Section 10 Mandate',
                desc: 'High-volume fiduciary subject to DPO appointment & audits.',
              },
              {
                id: 'data_processor',
                title: 'Data Processor',
                badge: 'Section 8 Contracted',
                desc: 'Processes personal data on behalf of a Data Fiduciary.',
              },
              {
                id: 'consent_manager',
                title: 'Consent Manager',
                badge: 'Section 6(7) Registered',
                desc: 'Registered platform enabling principals to manage consents.',
              },
            ].map((role) => (
              <div
                key={role.id}
                onClick={() => setComplianceRole(role.id as any)}
                className={`p-4 rounded-2xl border transition-all cursor-pointer ${
                  complianceRole === role.id
                    ? 'border-blue bg-blue-50/60 shadow-xs ring-1 ring-blue'
                    : 'border-line bg-white hover:border-slate-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold text-xs text-ink">{role.title}</span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-surface border border-line text-sub">
                    {role.badge}
                  </span>
                </div>
                <p className="text-[11.5px] text-sub mt-1.5 leading-relaxed">{role.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-err/20 bg-err/5 p-3.5 text-xs text-err font-medium flex items-center gap-2">
          <span>✕</span> {error}
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-line">
        <Button type="button" variant="btn-g" onClick={handleSkipOnboarding}>
          Skip Setup
        </Button>
        <Button
          type="submit"
          variant="btn-p"
          isLoading={isSubmitting}
          loadingText="Creating Organisation..."
          disabled={isSubmitting || !orgName.trim()}
        >
          Save &amp; Continue →
        </Button>
      </div>
    </form>
  );

  // ---------------------------------------------------------
  // Step 2 Render: Application Setup
  // ---------------------------------------------------------
  const renderStep2 = () => (
    <form onSubmit={handleStep2Submit} className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-ink">Step 2: First Application Setup</h2>
        <p className="text-xs text-sub mt-1">
          Each application (web portal, mobile app, microservice) gets its own isolated consent registers and API keys.
        </p>
      </div>

      <div className="space-y-4">
        <Input
          label="Application Name"
          type="text"
          placeholder="e.g. Web Customer Portal"
          value={appName}
          onChange={(e) => setAppName(e.target.value)}
          disabled={isSubmitting}
          required
          autoFocus
        />

        <Input
          label="Application Domain / Origin"
          type="text"
          placeholder="e.g. app.parakrama.com"
          value={appDomain}
          onChange={(e) => setAppDomain(e.target.value)}
          disabled={isSubmitting}
          helperText="Used for CORS origin validation during consent widget logging."
        />

        <div className="space-y-2">
          <label className="block text-xs font-semibold uppercase tracking-wider text-sub">
            Target Environment
          </label>
          <div className="grid grid-cols-3 gap-3">
            {[
              { id: 'production', label: 'Production', badge: 'Live API' },
              { id: 'staging', label: 'Staging', badge: 'Pre-release' },
              { id: 'development', label: 'Development', badge: 'Local Sandbox' },
            ].map((env) => (
              <div
                key={env.id}
                onClick={() => setEnvironment(env.id as any)}
                className={`p-3 rounded-xl border text-center transition-all cursor-pointer ${
                  environment === env.id
                    ? 'border-blue bg-blue-50/60 shadow-xs ring-1 ring-blue'
                    : 'border-line bg-white hover:border-slate-300'
                }`}
              >
                <div className="font-bold text-xs text-ink">{env.label}</div>
                <div className="text-[10px] text-muted mt-0.5">{env.badge}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-err/20 bg-err/5 p-3.5 text-xs text-err font-medium flex items-center gap-2">
          <span>✕</span> {error}
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-line">
        <Button type="button" variant="btn-g" onClick={handleBack}>
          ← Back
        </Button>
        <Button
          type="submit"
          variant="btn-p"
          isLoading={isSubmitting}
          loadingText="Creating Application..."
          disabled={isSubmitting || !appName.trim()}
        >
          Next Step →
        </Button>
      </div>
    </form>
  );

  // ---------------------------------------------------------
  // Step 3 Render: Default Notice Template Selection
  // ---------------------------------------------------------
  const currentTemplate = NOTICE_TEMPLATES[selectedTemplateKey] ?? NOTICE_TEMPLATES.customer!;

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-ink">Step 3: Multilingual Notice Template</h2>
        <p className="text-xs text-sub mt-1">
          Choose a pre-configured DPDP consent notice template supporting English &amp; Hindi multilingual rendering.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {Object.entries(NOTICE_TEMPLATES).map(([key, tmpl]) => (
          <div
            key={key}
            onClick={() => setSelectedTemplateKey(key)}
            className={`p-4 rounded-2xl border transition-all cursor-pointer ${
              selectedTemplateKey === key
                ? 'border-blue bg-blue-50/60 shadow-xs ring-1 ring-blue'
                : 'border-line bg-white hover:border-slate-300'
            }`}
          >
            <div className="font-bold text-xs text-ink">{tmpl.name}</div>
            <p className="text-[11px] text-sub mt-1 line-clamp-2">{tmpl.description}</p>
          </div>
        ))}
      </div>

      {/* Live Interactive Notice Preview Card */}
      <div className="rounded-2xl border border-line bg-white p-4 shadow-xs space-y-3">
        <div className="flex items-center justify-between pb-2 border-b border-line">
          <span className="text-xs font-bold text-ink flex items-center gap-2">
            <span>🌐</span> Live Multilingual Notice Preview
          </span>
          <div className="flex items-center gap-1 bg-surface p-1 rounded-lg border border-line text-xs font-medium">
            <button
              type="button"
              onClick={() => setNoticePreviewLang('en')}
              className={`px-2.5 py-1 rounded-md transition-all ${
                noticePreviewLang === 'en' ? 'bg-white shadow-xs font-bold text-blue' : 'text-sub'
              }`}
            >
              English (EN)
            </button>
            <button
              type="button"
              onClick={() => setNoticePreviewLang('hi')}
              className={`px-2.5 py-1 rounded-md transition-all ${
                noticePreviewLang === 'hi' ? 'bg-white shadow-xs font-bold text-blue' : 'text-sub'
              }`}
            >
              हिंदी (HI)
            </button>
          </div>
        </div>

        <div className="space-y-3 pt-1">
          <div className="text-sm font-extrabold text-ink">
            {currentTemplate.notice[noticePreviewLang].title}
          </div>

          <div className="space-y-2">
            {currentTemplate.notice[noticePreviewLang].purposes.map((p) => (
              <div key={p.id} className="p-3 rounded-xl border border-line bg-surface space-y-1">
                <div className="font-bold text-xs text-ink flex items-center justify-between">
                  <span>{p.title}</span>
                  <span className="text-[10px] mono text-blue bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100">
                    ID: {p.id}
                  </span>
                </div>
                <p className="text-[11.5px] text-sub">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-line">
        <Button type="button" variant="btn-g" onClick={handleBack}>
          ← Back
        </Button>
        <Button type="button" variant="btn-p" onClick={handleNext}>
          Next Step →
        </Button>
      </div>
    </div>
  );

  // ---------------------------------------------------------
  // Step 4 Render: Primary Purpose Definition
  // ---------------------------------------------------------
  const renderStep4 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-ink">Step 4: Data Principal Consent Purposes</h2>
        <p className="text-xs text-sub mt-1">
          Define the specific, informed processing purposes presented to your Data Principals under DPDP Section 6.
        </p>
      </div>

      {/* Purpose List */}
      <div className="space-y-3">
        {purposes.map((p) => (
          <div
            key={p.id}
            className="p-4 rounded-2xl border border-line bg-white shadow-xs space-y-2 transition-all hover:border-slate-300"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-bold text-xs text-ink">{p.title}</span>
                <span
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    p.isMandatory
                      ? 'bg-err/10 text-err border border-err/20'
                      : 'bg-emerald/10 text-emerald border border-emerald/20'
                  }`}
                >
                  {p.isMandatory ? 'Mandatory' : 'Optional (Opt-In)'}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleToggleMandatory(p.id)}
                  className="text-xs font-semibold text-sub hover:text-blue cursor-pointer"
                >
                  Toggle {p.isMandatory ? 'Optional' : 'Mandatory'}
                </button>
                <button
                  type="button"
                  onClick={() => handleDeletePurpose(p.id)}
                  className="text-xs font-semibold text-err hover:underline cursor-pointer"
                >
                  Delete
                </button>
              </div>
            </div>

            <p className="text-xs text-sub leading-relaxed">{p.description}</p>
            <div className="text-[10.5px] text-muted mono pt-1">
              Retention Period: {p.retentionDays} days · ID: {p.id}
            </div>
          </div>
        ))}
      </div>

      {/* Add Custom Purpose Form */}
      <div className="p-4 rounded-2xl border border-line bg-surface space-y-3">
        <span className="text-xs font-bold text-ink">Add Custom Consent Purpose</span>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            placeholder="Purpose Title (e.g. SMS Order Status Alerts)"
            value={newPurposeTitle}
            onChange={(e) => setNewPurposeTitle(e.target.value)}
          />
          <Input
            placeholder="Description for Data Principal"
            value={newPurposeDesc}
            onChange={(e) => setNewPurposeDesc(e.target.value)}
          />
        </div>
        <Button
          type="button"
          variant="btn-g"
          size="sm"
          onClick={handleAddPurpose}
          disabled={!newPurposeTitle.trim() || !newPurposeDesc.trim()}
        >
          + Add Purpose
        </Button>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-line">
        <Button type="button" variant="btn-g" onClick={handleBack}>
          ← Back
        </Button>
        <Button type="button" variant="btn-p" onClick={handleNext}>
          Next Step →
        </Button>
      </div>
    </div>
  );

  // ---------------------------------------------------------
  // Step 5 Render: Webhook Endpoint Configuration
  // ---------------------------------------------------------
  const renderStep5 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-ink">Step 5: Webhook Endpoint Configuration</h2>
        <p className="text-xs text-sub mt-1">
          Receive real-time push events when Data Principals grant or withdraw consent under DPDP Section 6(4).
        </p>
      </div>

      <div className="space-y-4">
        <Input
          label="Webhook Endpoint URL"
          type="url"
          placeholder="https://api.yourcompany.com/webhooks/dpdp-events"
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
        />

        <div className="space-y-1.5">
          <label className="block text-xs font-semibold uppercase tracking-wider text-sub">
            Signing Secret Key
          </label>
          <div className="flex items-center gap-2 p-3 rounded-xl border border-line bg-surface font-mono text-xs text-ink">
            <span className="flex-1 truncate">
              {showWebhookSecret ? webhookSecret : '••••••••••••••••••••••••••••••••••••••••'}
            </span>
            <button
              type="button"
              onClick={() => setShowWebhookSecret(!showWebhookSecret)}
              className="text-xs text-sub hover:text-ink font-semibold"
            >
              {showWebhookSecret ? 'Hide' : 'Reveal'}
            </button>
          </div>
        </div>

        {/* Event Subscriptions Checkboxes */}
        <div className="space-y-2">
          <label className="block text-xs font-semibold uppercase tracking-wider text-sub">
            Subscribed Real-time Events
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              { id: 'consent.granted', label: 'consent.granted (Opt-In Recorded)' },
              { id: 'consent.withdrawn', label: 'consent.withdrawn (Opt-Out / Withdrawal)' },
              { id: 'notice.updated', label: 'notice.updated (New Version Published)' },
              { id: 'data_principal.erasure_requested', label: 'data_principal.erasure_requested' },
            ].map((item) => (
              <label
                key={item.id}
                className="flex items-center gap-2.5 p-3 rounded-xl border border-line bg-white text-xs font-medium text-ink cursor-pointer hover:border-slate-300"
              >
                <input
                  type="checkbox"
                  checked={webhookEvents.includes(item.id)}
                  onChange={() => handleToggleWebhookEvent(item.id)}
                  className="rounded border-line text-blue focus:ring-blue"
                />
                <span className="mono text-[11px]">{item.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Test Ping Webhook Trigger */}
        <div className="p-4 rounded-2xl border border-line bg-surface space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-ink">Test Endpoint Payload Dispatch</span>
            <Button
              type="button"
              variant="btn-g"
              size="sm"
              isLoading={testWebhookStatus === 'testing'}
              loadingText="Testing Ping..."
              onClick={handleTestWebhookPing}
            >
              Send Test Webhook Ping
            </Button>
          </div>

          {testWebhookStatus === 'success' && (
            <div className="rounded-xl border border-ok/20 bg-ok/5 p-3 text-xs text-ok font-medium flex items-center gap-2">
              <span>✓</span> Test event sent! Endpoint returned HTTP 200 OK (34ms).
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-line">
        <Button type="button" variant="btn-g" onClick={handleBack}>
          ← Back
        </Button>
        <div className="flex items-center gap-2">
          <Button type="button" variant="btn-g" onClick={handleNext}>
            Skip Webhooks
          </Button>
          <Button type="button" variant="btn-p" onClick={handleNext}>
            Next Step →
          </Button>
        </div>
      </div>
    </div>
  );

  // ---------------------------------------------------------
  // Step 6 Render: API Credentials & SDK Snippet Summary
  // ---------------------------------------------------------
  const displayAppId = createdAppId || 'app_demo_12345';
  const displayApiKey = createdApiKey || getApiKey() || 'dpdp_live_demo_key_998877';

  const renderStep6 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-ink">Step 6: API Credentials &amp; Integration SDK</h2>
        <p className="text-xs text-sub mt-1">
          Your workspace and application are ready! Copy your API key or download credentials JSON to start integrating.
        </p>
      </div>

      {/* API Credentials Card */}
      <div className="rounded-2xl border border-line bg-white p-5 shadow-xs space-y-4">
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold uppercase tracking-wider text-sub">
            Application ID
          </label>
          <div className="p-3 rounded-xl border border-line bg-surface font-mono text-xs text-ink truncate">
            {displayAppId}
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="block text-xs font-semibold uppercase tracking-wider text-sub">
            Live API Key
          </label>
          <div className="flex items-center gap-2 p-3 rounded-xl border border-line bg-surface font-mono text-xs text-ink">
            <span className="flex-1 truncate">
              {showApiKey ? displayApiKey : '••••••••••••••••••••••••••••••••••••••••'}
            </span>
            <button
              type="button"
              onClick={() => setShowApiKey(!showApiKey)}
              className="text-xs font-semibold text-sub hover:text-ink"
            >
              {showApiKey ? 'Hide' : 'Reveal'}
            </button>
            <button
              type="button"
              onClick={handleCopyApiKey}
              className="text-xs font-bold text-blue hover:underline"
            >
              {copiedKey ? 'Copied! ✓' : 'Copy Key'}
            </button>
          </div>
        </div>

        <Button
          type="button"
          variant="btn-g"
          fullWidth
          onClick={handleDownloadCredentials}
          leftIcon={
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          }
        >
          Download Credentials JSON (dpdp_credentials.json)
        </Button>
      </div>

      {/* Code Snippets Tabbed Container */}
      <div className="rounded-2xl border border-line bg-[#0B0F17] text-white p-5 space-y-3 font-mono text-xs overflow-hidden">
        <div className="flex items-center justify-between pb-3 border-b border-gray-800">
          <div className="flex items-center gap-2">
            {[
              { id: 'sdk', label: 'npm @dpdp/sdk' },
              { id: 'widget', label: 'HTML Script Tag' },
              { id: 'curl', label: 'cURL API' },
              { id: 'python', label: 'Python SDK' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveCodeTab(tab.id as any)}
                className={`px-3 py-1.5 rounded-lg text-xs font-sans transition-all cursor-pointer ${
                  activeCodeTab === tab.id
                    ? 'bg-blue text-white font-bold'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-2 overflow-x-auto text-[11.5px] leading-relaxed text-gray-300">
          {activeCodeTab === 'sdk' && (
            <pre>
              {`import { ConsentClient } from '@dpdp/sdk';

const client = new ConsentClient({
  apiKey: '${displayApiKey}',
  applicationId: '${displayAppId}'
});

// Record user consent
const record = await client.recordConsent({
  principalId: 'user_9921',
  noticeVersion: 1,
  purposes: ['account_management', 'product_analytics']
});`}
            </pre>
          )}

          {activeCodeTab === 'widget' && (
            <pre>
              {`<!-- Embed Consentry Consent Banner -->
<script 
  src="https://cdn.dpdp-consent.com/v1/widget.js"
  data-app-id="${displayAppId}"
  data-api-key="${displayApiKey}"
  data-lang="en"
  async>
</script>`}
            </pre>
          )}

          {activeCodeTab === 'curl' && (
            <pre>
              {`curl -X POST https://api.dpdp-consent.com/v1/consent/record \\
  -H "Authorization: Bearer ${displayApiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "principalId": "user_9921",
    "noticeVersion": 1,
    "purposes": ["account_management"]
  }'`}
            </pre>
          )}

          {activeCodeTab === 'python' && (
            <pre>
              {`import requests

url = "https://api.dpdp-consent.com/v1/consent/record"
headers = {
    "Authorization": "Bearer ${displayApiKey}",
    "Content-Type": "application/json"
}
payload = {
    "principalId": "user_9921",
    "noticeVersion": 1,
    "purposes": ["account_management"]
}
response = requests.post(url, json=payload, headers=headers)`}
            </pre>
          )}
        </div>
      </div>

      <div className="pt-2">
        <Button
          type="button"
          variant="btn-p"
          fullWidth
          size="lg"
          onClick={handleCompleteOnboarding}
        >
          Complete Setup &amp; Enter Dashboard →
        </Button>
      </div>
    </div>
  );

  // ---------------------------------------------------------
  // Main Stepper Layout & Progress Header
  // ---------------------------------------------------------
  const totalSteps = 6;
  const progressPercent = Math.round((currentStep / totalSteps) * 100);

  const stepTitles = [
    'Organization & Role',
    'Application Setup',
    'Notice Template',
    'Consent Purposes',
    'Webhooks Configuration',
    'API Keys & SDK',
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#111111] bg-sg flex flex-col justify-between p-4 sm:p-6 font-sans antialiased">
      {/* Header */}
      <header className="max-w-4xl w-full mx-auto flex items-center justify-between py-3">
        <Link to="/" className="flex items-center gap-2.5 font-semibold text-base">
          <span className="w-8 h-8 rounded-lg bg-blue flex items-center justify-center text-white shadow-sm shrink-0">
            <svg
              className="w-4.5 h-4.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          </span>
          <span className="font-bold text-lg text-ink">Consentry</span>
        </Link>
        <button
          type="button"
          onClick={handleSkipOnboarding}
          className="text-xs font-semibold text-sub hover:text-ink px-3 py-1.5 rounded-lg hover:bg-slate-100"
        >
          Exit Setup ✕
        </button>
      </header>

      {/* Main Wizard Card */}
      <main className="flex-1 flex items-center justify-center py-6 px-2">
        <div className="w-full max-w-3xl bg-white border border-line rounded-3xl p-6 sm:p-10 shadow-cardhover relative overflow-hidden">
          {/* Stepper Header Bar */}
          <div className="mb-8 space-y-3">
            <div className="flex items-center justify-between text-xs font-bold text-sub">
              <span className="mono uppercase tracking-wider text-blue">
                Step {currentStep} of {totalSteps}: {stepTitles[currentStep - 1]}
              </span>
              <span className="mono text-muted">{progressPercent}% Completed</span>
            </div>

            {/* Visual Step Progress Fill Bar */}
            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden flex">
              <div
                className="bg-gradient-to-r from-blue via-indigo-500 to-emerald h-full transition-all duration-500 rounded-full"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            {/* Visual Step Number Circles */}
            <div className="hidden sm:flex items-center justify-between pt-1">
              {stepTitles.map((title, idx) => {
                const stepNum = idx + 1;
                const isDone = stepNum < currentStep;
                const isCurrent = stepNum === currentStep;

                return (
                  <div key={title} className="flex items-center gap-1.5 text-xs">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold transition-all ${
                        isDone
                          ? 'bg-ok text-white'
                          : isCurrent
                            ? 'bg-blue text-white ring-4 ring-blue-50'
                            : 'bg-surface border border-line text-muted'
                      }`}
                    >
                      {isDone ? '✓' : stepNum}
                    </div>
                    <span
                      className={`text-[11px] font-medium hidden md:inline ${
                        isCurrent ? 'text-ink font-bold' : isDone ? 'text-sub' : 'text-muted'
                      }`}
                    >
                      {title}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Active Step Form Content */}
          <div>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep5()}
            {currentStep === 6 && renderStep6()}
          </div>
        </div>
      </main>

      <footer className="max-w-3xl w-full mx-auto text-center py-3">
        <p className="text-[11px] text-muted font-medium">
          Consentry · DPDP Act 2023 Consent Infrastructure &amp; Onboarding Wizard
        </p>
      </footer>
    </div>
  );
}

export const Onboarding = OnboardingPage;
